import React from 'react';
import { History, GitCommit, Download } from 'lucide-react';

/**
 * Devis page — versions timeline rendered inside the DetailSheet footer.
 * Renders nothing when there are no versions. Restoration is only allowed on
 * non-current versions of quotes that aren't terminal (Accepté/Converti/Refusé).
 */
export default function QuoteVersionsTimeline({ versions, detail, onDownloadPdf, onRestore }) {
  if (!versions || versions.length === 0) return null;
  return (
    <div className="pt-3 border-t border-[#EFF0F6]" data-testid="devis-versions">
      <div className="flex items-center gap-1.5 mb-2">
        <History className="w-3.5 h-3.5 text-[#4F46E5]" />
        <span className="text-[11.5px] font-semibold text-[#6B6C85] uppercase tracking-wide">Versions ({versions.length})</span>
      </div>
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {versions.map((v, idx) => {
          const isCurrent = idx === 0; // versions are returned DESC
          const prev = versions[idx + 1];
          const delta = prev ? (Number(v.amount_ttc) - Number(prev.amount_ttc)) : 0;
          const canRestore = !isCurrent && !['Accepté', 'Converti', 'Refusé'].includes(detail.status);
          return (
            <div
              key={v.id}
              data-testid={`devis-version-${v.version}`}
              className={`relative pl-5 pr-2 py-2 rounded-lg border ${isCurrent ? 'border-[#C7D2FE] bg-[#FAF6FF]' : 'border-[#EFF0F6] bg-white hover:bg-[#FAFAFE]'}`}
            >
              <span className={`absolute left-1.5 top-3 w-2.5 h-2.5 rounded-full ${isCurrent ? 'bg-[#4F46E5]' : 'bg-[#D4D5E0]'}`} />
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <GitCommit className="w-3 h-3 text-[#4F46E5]" />
                    <span className="text-[12.5px] font-bold text-[#1A1B2E]">v{v.version}</span>
                    {isCurrent && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9px] font-bold tracking-wide">ACTUELLE</span>}
                    <span className="text-[11px] text-[#8E8FA5] truncate">· {new Date(v.changed_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} · {v.changed_by_name || 'système'}</span>
                  </div>
                  <div className="text-[12px] text-[#4A4B63] leading-tight">{v.change_summary}</div>
                  <div className="mt-0.5 text-[12.5px] font-bold text-[#1A1B2E]">
                    {Number(v.amount_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € TTC
                    {delta !== 0 && (
                      <span className={`ml-1.5 text-[10.5px] font-semibold ${delta > 0 ? 'text-[#0F766E]' : 'text-[#B91C1C]'}`}>
                        {delta > 0 ? '+' : ''}{delta.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onDownloadPdf(v.version)}
                    title="Télécharger le PDF de cette version"
                    data-testid={`devis-version-pdf-${v.version}`}
                    className="w-8 h-8 rounded-lg bg-white border border-[#EFF0F6] text-[#6B6C85] hover:bg-[#F6F7FB] flex items-center justify-center"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  {canRestore && (
                    <button
                      type="button"
                      onClick={() => onRestore(v.version)}
                      title={`Restaurer la version ${v.version}`}
                      data-testid={`devis-version-restore-${v.version}`}
                      className="h-8 px-2 rounded-lg bg-[#EEF2FF] border border-[#C7D2FE] text-[#4F46E5] text-[11px] font-semibold hover:bg-[#EDE8FF]"
                    >
                      Restaurer
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
